import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Handle OAuth sign-up with invite code
        if (event === 'SIGNED_IN' && session?.user) {
          const pendingCode = sessionStorage.getItem('pendingInviteCode');
          if (pendingCode) {
            // Check if this is a new user (OAuth sign-up)
            const { data: existingUsage } = await supabase
              .from('user_token_usage')
              .select('user_id')
              .eq('user_id', session.user.id)
              .single();

            // If no existing usage record, this is likely a new OAuth sign-up
            if (!existingUsage) {
              await supabase.rpc('use_invite_code', {
                p_code: pendingCode,
                p_user_id: session.user.id
              });
            }

            // Clear the pending code
            sessionStorage.removeItem('pendingInviteCode');
          }
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    });
    if (error) {
      console.error('Error signing in with Google:', error.message);
      throw error;
    }
  };

  const signInWithGithub = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: window.location.origin
      }
    });
    if (error) {
      console.error('Error signing in with GitHub:', error.message);
      throw error;
    }
  };

  const signInWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) {
      console.error('Error signing in with email:', error.message);
      throw error;
    }
    return data;
  };

  const signUpWithEmail = async (email, password, inviteCode) => {
    // First, validate the invite code
    if (!inviteCode) {
      throw new Error('Invite code is required');
    }

    const { data: codeData, error: codeError } = await supabase
      .from('invite_codes')
      .select('*')
      .eq('code', inviteCode)
      .single();

    if (codeError || !codeData) {
      throw new Error('Invalid invite code');
    }

    // Check if code is still valid
    if (codeData.current_uses >= codeData.max_uses) {
      throw new Error('Invite code has been fully used');
    }

    if (codeData.expires_at && new Date(codeData.expires_at) < new Date()) {
      throw new Error('Invite code has expired');
    }

    // Create the account
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin
      }
    });

    if (error) {
      console.error('Error signing up with email:', error.message);
      throw error;
    }

    // Mark the invite code as used
    if (data.user) {
      await supabase.rpc('use_invite_code', {
        p_code: inviteCode,
        p_user_id: data.user.id
      });
    }

    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error.message);
      throw error;
    }
  };

  const value = {
    user,
    session,
    loading,
    signInWithGoogle,
    signInWithGithub,
    signInWithEmail,
    signUpWithEmail,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
